import { useLayoutEffect, useState } from "react";
// Self-sufficient: pulls the shared `.clinical-document-preview` / page styling.
import "./ClinicalEditor.scss";

/**
 * PaginatedClinicalDocument
 *
 * Read-only viewer that splits a clinical-document HTML string into real A4
 * pages so the on-screen preview matches the paginated PDF (instead of one
 * infinitely-growing sheet).
 *
 * It renders the HTML once off-screen using the exact `.clinical-document-preview`
 * styling, measures the top-level blocks, and distributes them across pages —
 * splitting an over-tall table at row level (repeating its header) so content
 * never straddles a page gap. Any failure falls back to a single sheet, so the
 * output is never worse than the previous behaviour.
 *
 * Intended for previews only. The editable TipTap editor and the html2pdf
 * export path are deliberately NOT routed through here.
 */

// A4 in px @ ~96dpi — matches `.clinical-document-preview` (210mm × 297mm, 48px padding).
const A4_W = 794;
const A4_H = 1123;
const PAD = 48;
const CONTENT_H = A4_H - PAD * 2; // ~1027px usable height per page

const CONTAINER_TAGS = new Set(["DIV", "SECTION", "ARTICLE", "MAIN", "BODY"]);
const HEAD_TAGS = new Set(["STYLE", "LINK", "META"]);

/** Height of an element including its vertical margins. */
const outerHeight = (el) => {
  const rect = el.getBoundingClientRect();
  const style = window.getComputedStyle(el);
  return (
    rect.height +
    (parseFloat(style.marginTop) || 0) +
    (parseFloat(style.marginBottom) || 0)
  );
};

/**
 * Splits a table taller than one page into multiple table elements, each
 * sized to fit `maxH`. Header rows (a leading run of <th>-only rows) are
 * repeated on every slice. Returns an array of detached <table> nodes.
 */
const splitTable = (table, maxH, measurer) => {
  const rows = Array.from(table.rows); // all <tr> in document order
  const headerRows = [];
  let i = 0;
  while (i < rows.length && rows[i].querySelector("th") && !rows[i].querySelector("td")) {
    headerRows.push(rows[i]);
    i++;
  }
  const bodyRows = rows.slice(i);
  if (!bodyRows.length) return [table.cloneNode(true)];

  const makeShell = () => {
    const shell = table.cloneNode(false); // <table> with its attrs, no rows
    const tbody = document.createElement("tbody");
    headerRows.forEach((hr) => tbody.appendChild(hr.cloneNode(true)));
    shell.appendChild(tbody);
    return { shell, tbody };
  };

  const slices = [];
  let idx = 0;
  while (idx < bodyRows.length) {
    const { shell, tbody } = makeShell();
    measurer.appendChild(shell);

    let added = 0;
    while (idx < bodyRows.length) {
      const rowClone = bodyRows[idx].cloneNode(true);
      tbody.appendChild(rowClone);
      // Always keep at least one body row per slice; otherwise stop when full.
      if (added > 0 && outerHeight(shell) > maxH) {
        tbody.removeChild(rowClone); // doesn't fit — carry to next slice
        break;
      }
      added++;
      idx++;
    }

    measurer.removeChild(shell);
    slices.push(shell);
  }
  return slices;
};

/** Re-wrap a page's nodes, preserving the source wrapper + any shared head nodes. */
const buildPageHtml = (nodes, wrapperTemplate, sharedHeadNodes) => {
  const heads = sharedHeadNodes.map((n) => n.cloneNode(true));
  if (wrapperTemplate) {
    const wrap = wrapperTemplate.cloneNode(false); // keep tag + inline styles, drop children
    heads.forEach((n) => wrap.appendChild(n));
    nodes.forEach((n) => wrap.appendChild(n));
    return wrap.outerHTML;
  }
  const tmp = document.createElement("div");
  heads.forEach((n) => tmp.appendChild(n));
  nodes.forEach((n) => tmp.appendChild(n));
  return tmp.innerHTML;
};

/** Core pagination: HTML string in, array of per-page HTML strings out. */
const paginate = (html) => {
  const measurer = document.createElement("div");
  measurer.className = "clinical-document-preview";
  Object.assign(measurer.style, {
    position: "absolute",
    left: "-10000px",
    top: "0",
    visibility: "hidden",
    height: "auto",
    minHeight: "0",
    width: `${A4_W}px`,
  });
  measurer.innerHTML = html;
  document.body.appendChild(measurer);

  try {
    // Descend through a single generic wrapper (e.g. parseSummaryJsonToHtml's
    // font <div>) so we paginate its blocks, not the wrapper itself.
    let blockParent = measurer;
    let wrapperTemplate = null;
    if (
      measurer.children.length === 1 &&
      CONTAINER_TAGS.has(measurer.firstElementChild.tagName)
    ) {
      wrapperTemplate = measurer.firstElementChild;
      blockParent = wrapperTemplate;
    }

    const allBlocks = Array.from(blockParent.children);
    // <style>/<link>/<meta> carry styling for the whole doc — repeat on every page.
    const sharedHeadNodes = allBlocks.filter((b) => HEAD_TAGS.has(b.tagName));
    const blocks = allBlocks.filter((b) => !HEAD_TAGS.has(b.tagName));

    const pages = [];
    let current = [];
    let currentH = 0;
    const flush = () => {
      if (current.length) {
        pages.push(current);
        current = [];
        currentH = 0;
      }
    };

    const isPageBreakBlock = (el) => {
      if (!el) return false;
      if (el.classList && (el.classList.contains("html2pdf__page-break") || el.classList.contains("page-break"))) {
        return true;
      }
      const style = el.getAttribute ? el.getAttribute("style") || "" : "";
      return /page-break-before\s*:\s*always/i.test(style) || /break-before\s*:\s*(page|always)/i.test(style);
    };

    blocks.forEach((block) => {
      if (isPageBreakBlock(block)) {
        flush();
        // If it's a dedicated zero-content break marker div, do not push as content onto the new page
        if (
          block.classList &&
          (block.classList.contains("html2pdf__page-break") || block.classList.contains("page-break")) &&
          !block.textContent.trim() &&
          !block.querySelector("img, table, svg, canvas")
        ) {
          return;
        }
      }

      const h = outerHeight(block);

      if (h <= CONTENT_H) {
        if (currentH + h > CONTENT_H && current.length) flush();
        current.push(block.cloneNode(true));
        currentH += h;
        return;
      }

      // Block taller than a full page.
      flush();
      if (block.tagName === "TABLE") {
        splitTable(block, CONTENT_H, measurer).forEach((slice) => pages.push([slice]));
      } else {
        // Rare: an unsplittable block bigger than a page — its own (overflowing) page.
        pages.push([block.cloneNode(true)]);
      }
    });
    flush();

    if (!pages.length) return [html];
    return pages.map((nodes) => buildPageHtml(nodes, wrapperTemplate, sharedHeadNodes));
  } finally {
    document.body.removeChild(measurer);
  }
};

const PaginatedClinicalDocument = ({ html }) => {
  // null = not yet paginated (render an un-clipped single sheet to avoid a flash).
  const [pages, setPages] = useState(null);

  useLayoutEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!html || !html.trim()) {
        setPages([]);
        return;
      }
      // Wait for webfonts so measured heights match the painted result.
      try {
        if (document.fonts && document.fonts.ready) await document.fonts.ready;
      } catch (_) {
        /* fonts API unavailable — proceed */
      }
      if (cancelled) return;
      try {
        const result = paginate(html);
        if (!cancelled) setPages(result.length ? result : [html]);
      } catch (err) {
        console.warn("[PaginatedDocument] pagination failed, rendering single sheet:", err);
        if (!cancelled) setPages([html]);
      }
    };

    setPages(null); // reset to the pre-pagination sheet whenever the html changes
    run();
    return () => {
      cancelled = true;
    };
  }, [html]);

  if (!html || !html.trim()) return null;

  // Pre-pagination: one sheet WITHOUT the fixed-height page class, so tall
  // content isn't clipped before measurement completes.
  if (pages === null) {
    return (
      <div className="paginated-document">
        <div
          className="clinical-document-preview"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    );
  }

  if (!pages.length) return null;

  return (
    <div className="paginated-document">
      {pages.map((p, i) => (
        <div
          key={i}
          className="clinical-document-preview clinical-document-page"
          dangerouslySetInnerHTML={{ __html: p }}
        />
      ))}
    </div>
  );
};

export default PaginatedClinicalDocument;
